import { GraphQLResult } from '@aws-amplify/api-graphql';
import { API, Auth, graphqlOperation } from 'aws-amplify';
import _ from 'lodash';
import {
  CreateUserInput,
  CreateUserMutation,
  GetUserPlanQuery,
  GetUserQuery,
  ListUserAppointmentsByUserIdQuery,
  ListUserAssessmentLogByUserIdQuery,
  RolesEnum,
  UpdateUserInput,
  UpdateUserMutation,
  User,
  UserMetadataInput,
  UserStatusEnum,
} from '../API';
import { createUser } from '../graphql/custom_mutations';
import { updateUser } from '../graphql/mutations';
import {
  getEventAttendeeByUserId,
  getUser as findUser,
  listUserAppointmentsByUserId,
  listUserAssessmentLogByUserId,
  listUserPlansByUserId,
  logEvent,
} from '../graphql/queries';
import { cleanPhoneNumber } from '../utils';

export const saveUser = async (input: CreateUserInput | UpdateUserInput, options?: { forceCreate?: boolean }) => {
  try {
    const { forceCreate = false } = options ?? {};

    if (!forceCreate && input.id !== null) {
      const req = (await API.graphql(
        graphqlOperation(updateUser, {
          input,
        })
      )) as GraphQLResult<UpdateUserMutation>;
      return req?.data?.updateUser;
    } else {
      const req = (await API.graphql({
        query: createUser,
        variables: { input },
        authMode: 'API_KEY',
      })) as GraphQLResult<CreateUserMutation>;
      return req?.data?.createUser;
    }
  } catch (err) {
    return err;
  }
};

export const getAllUserAppointments = async (userId: string) => {
  try {
    const req = (await API.graphql(
      graphqlOperation(listUserAppointmentsByUserId, { userId, sortDirection: 'DESC' })
    )) as GraphQLResult<ListUserAppointmentsByUserIdQuery>;
    return req?.data?.listUserAppointmentsByUserId?.items;
  } catch (err) {
    return err;
  }
};
export const getAllUserAssessments = async (userId: string) => {
  let req: GraphQLResult<ListUserAssessmentLogByUserIdQuery> = {};
  try {
    req = (await API.graphql(
      graphqlOperation(listUserAssessmentLogByUserId, { userId, sortDirection: 'DESC' })
    )) as GraphQLResult<ListUserAssessmentLogByUserIdQuery>;
  } catch (err: any) {
    req = err;
  }
  return req?.data?.listUserAssessmentLogByUserId?.items;
};

export const getUser = async (id: string | undefined): Promise<GetUserQuery['getUser'] | boolean | unknown> => {
  if (!id || id === '') return false;
  let req: GraphQLResult<GetUserQuery> = {};
  try {
    req = (await API.graphql(
      graphqlOperation(findUser, {
        id,
      })
    )) as GraphQLResult<GetUserQuery>;
  } catch (err: any) {
    //silence
    req = err;
  }

  return req?.data?.getUser;
};

/**
 * This method creates an icognito account and signs the user in immediatley
 * @param input
 * @returns
 */
export const createAuthAccount = async (input: {
  email: string;
  password: string;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): Promise<User | unknown> => {
  try {
    const cognitoUser = await Auth.signUp({
      username: input?.email,
      password: input?.password,
      attributes: {
        email: input.email,
        phone_number: input?.phone ? '+1' + cleanPhoneNumber(input?.phone) : '',
        name: `${input?.firstName} ${input?.lastName}`,
      },
    });

    //we need to add the user to the user database
    const newUser = await saveUser(
      {
        id: cognitoUser.userSub,
        firstName: input.firstName ?? null,
        lastName: input.lastName ?? null,
        email: input.email,
        roles: [RolesEnum.MEMBER],
        phone: cleanPhoneNumber(input?.phone),
        status: UserStatusEnum.ACTIVE,
        createdAt: new Date().toISOString(),
        referralSource: 'WebbApp',
        metadata: {
          lastLogin: Math.round(Date.now() / 1000),
        },
      },
      { forceCreate: true }
    );

    //signin the user and send it back
    await signinUser({
      username: input?.email,
      password: input?.password,
    }).catch((err) => err);

    return newUser as User;
  } catch (err) {
    console.log(err);
    return err;
  }
};

export const signinUser = async ({ username, password }: { username: string; password: string }): Promise<any> => {
  const auth = await Auth.signIn(username, password);
  return await getUser(auth?.username);
};

export const signoutUser = async () => {
  return await Auth.signOut();
};

export const resetPassword = async (email: string): Promise<any> => {
  // Send confirmation code to user's email
  try {
    await Auth.forgotPassword(email);
    return true;
  } catch (err) {
    return false;
  }
};

export const validatePasswordReset = async (email: string, code: string, newPassword: string): Promise<any> => {
  if (!email || !code || !newPassword) return false;
  // Collect confirmation code and new password, then
  try {
    await Auth.forgotPasswordSubmit(email, code, newPassword);
    return true;
  } catch (err) {
    return false;
  }
};

export const updateUserMetadata = async (userId: string, key: string, value: string): Promise<UpdateUserMutation['updateUser'] | unknown> => {
  //we will first get the users current metadata
  const getCurrentUserMeta = await getUser(userId).catch((err) => err);
  const { id, metadata } = getCurrentUserMeta as UpdateUserInput;
  return saveUser({
    id,
    metadata: { ...(metadata as UserMetadataInput), ...{ [key]: value } },
  });
};

export const authUserExists = async (username: string): Promise<boolean | null> => {
  let res = true;
  await Auth.signIn(username, '0009e6df-de8a-4d0a-8c80-a81cb628eddb').catch((err) => {
    switch (err.code) {
      case 'UserNotFoundException':
        res = false;
        break;
    }
  });

  return res;
};

export const getAllUserPlans = async (userId: string, active = true): Promise<GetUserPlanQuery['getUserPlan'][] | boolean> => {
  if (!userId || userId === '') return false;
  try {
    let filter;
    if (active) filter = { filter: { active: { eq: 'TRUE' } } };
    const req = (await API.graphql(
      graphqlOperation(listUserPlansByUserId, {
        userId,
        ...filter,
        sortDirection: 'DESC',
      })
    )) as GraphQLResult<GetUserPlanQuery>;
    return _.get(req, 'data.listUserPlansByUserId.items', []);
  } catch (err) {
    /* @ts-ignore */
    return err;
  }
};

export const setConboardingCompleted = async (userId: string) => {
  if (!userId || userId === '') return false;
  try {
    await API.graphql(graphqlOperation(logEvent, { eventName: 'USER_ONBOARDING_COMPLETED', message: JSON.stringify({ user: userId }) }));
    return true;
  } catch (err) {
    return false;
  }
};

export const setISSCompleted = async (userId: string) => {
  if (!userId || userId === '') return false;
  try {
    await updateUserMetadata(userId, 'issCompleted', 'TRUE');
    return true;
  } catch (err) {
    return false;
  }
};

export const getAllUserEventSessions = async (userId: string, eventId: string | null = null) => {
  if (!userId || userId === '') return false;
  let filter = {};
  try {
    if (typeof eventId === 'string') filter = { filter: { eventId: { eq: eventId } } };
    const req = await API.graphql(graphqlOperation(getEventAttendeeByUserId, { userId, ...filter }));
    return _.get(req, ['data', 'getEventAttendeeByUserId', 'items'], []);
  } catch (err) {
    return err;
  }
};
